import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Dices, Gift, Home, Menu, Phone, PlayCircle, Plus,
  Rocket, ShieldCheck, Target, Ticket, TicketPercent, Trophy, Wallet, X,
} from 'lucide-react';
import Logo from './Logo';
import AnnouncementMarquee from './AnnouncementMarquee';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import UserDropdown from './UserDropdown';
import AuthModal from './AuthModal';
import CallbackModal from './CallbackModal';
import MobileBottomNav from './MobileBottomNav';
import { useHiddenCategories } from '../lib/gameCategories';
import { brandName } from '../lib/brand';
import { adminShortcutVisible } from '../lib/adminDomain';
// Panelden yazılan CSS'in tutunacağı sabit kimlikler (bkz. lib/domHooks.js):
// kimlik şemaya gömülmez, ortak sabitten okunur.
import { SHELL_IDS } from '../lib/domHooks';
import useAnnouncement from '../lib/useAnnouncement';
import { bonusWalletTrigger } from './wallet/BonusWalletPanel';

const navItems = [
  { to: '/', label: 'Lobi', icon: Home },
  { to: '/spor', label: 'Spor', icon: Trophy },
  { to: '/slotlar', label: 'Slotlar', icon: Dices, category: 'slot' },
  { to: '/canli-casino', label: 'Casino', icon: PlayCircle, category: 'live-casino' },
  { to: '/crash', label: 'Crash', icon: Rocket, category: 'crash' },
  { to: '/promosyonlar', label: 'Bonus', icon: Gift },
  { to: '/promosyon-kodu', label: 'Promo Kod', icon: TicketPercent },
  // Çekiliş bir kategori değildir: yayında bir çekiliş varken görünür.
  // ``raffle_active`` bir ayar değil çalışma anındaki durumdur ve
  // ``/settings`` yanıtından gelir (bkz. CLAUDE.md → Çekiliş).
  { to: '/cekilis', label: 'Çekiliş', icon: Ticket, flag: 'raffle_active' },
  // Tahmin etkinliği de aynı kalıptadır: yayında etkinlik varken görünür.
  { to: '/etkinlik', label: 'Tahmin', icon: Target, flag: 'prediction_active' },
];

const Header = () => {
  const { user, balance, bonusBalance } = useAuth();
  const settings = useSettings();
  // Panel başka bir alan adına taşındıysa kısayol çizilmez: adresi
  // bilmediğimiz için bağlantı kurulamaz ve panelin varlığını
  // duyurmanın da anlamı yoktur (bkz. lib/adminDomain.js).
  const showAdminLink = adminShortcutVisible(settings);
  // Kapatılan ve kataloğu boş olan kategoriler menüde gösterilmez.
  const disabledCats = useHiddenCategories();

  const sportsVisible = settings.settingsLoaded && settings.sports_enabled !== false;
  const visibleNav = navItems.filter((n) => {
    if (n.to === '/spor') return sportsVisible;
    if (n.flag) return settings[n.flag] === true;
    return !n.category || !disabledCats.includes(n.category);
  });
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);
  // Panelden kapatıldığında "Beni Ara" butonu hiç çıkmaz.
  const callbackEnabled = settings.callback_enabled !== false;
  // Varsayılan duyuru marka adını içerir; marka henüz bilinmiyorken yazılırsa
  // bant " — En Çok Kazandıran…" diye başsız görünür. O ana kadar boş kalır.
  const announcement = useAnnouncement(
    settings,
    brandName(settings) ? '{{site_name}} — En Çok Kazandıran Bahis ve Casino Sitesi' : '',
  );
  const nav = useNavigate();
  const location = useLocation();

  const openAuth = (t) => { setAuthTab(t); setAuthOpen(true); };

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('reset_token')) {
      setAuthTab('reset');
      setAuthOpen(true);
    }
  }, []);

  useEffect(() => {
    if (location.state?.auth !== 'login' || user) return;
    setAuthTab('login');
    setAuthOpen(true);
    nav(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, nav, user]);

  return (
    <>
      <div className="site-announcement w-full text-xs border-b border-white/5">
        <div className="site-topbar max-w-7xl mx-auto px-3 sm:px-4 flex items-center gap-4 text-gray-300">
          {callbackEnabled && (
            <button onClick={() => setCallbackOpen(true)} className="announcement-callback flex shrink-0 items-center gap-1.5 hover:text-[var(--pusu-gold)]">
              <Phone size={12} /> Beni Ara
            </button>
          )}
          <div className="announcement-track min-w-0 flex-1 overflow-hidden">
            <AnnouncementMarquee text={announcement} />
          </div>
        </div>
      </div>
      <header id={SHELL_IDS.header} className="site-main-header sticky top-0 z-40 border-b border-white/5" style={{ background: 'var(--pusu-header-bg)', backdropFilter: 'blur(14px)' }}>
        <div className="site-header-row max-w-[1560px] mx-auto px-3 sm:px-4 h-16 md:h-[72px] flex xl:grid xl:grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 md:gap-3">
          <div className="flex min-w-0 items-center gap-2 md:gap-3 xl:justify-self-start">
            <button onClick={() => setMobileOpen(true)} className="side-icon-btn inline-flex xl:hidden shrink-0" style={{ width: 40, height: 40 }} aria-label="Menüyü aç" aria-expanded={mobileOpen}><Menu size={18} /></button>
            <span className="site-header-logo"><Logo /></span>
          </div>

          <nav className="hidden xl:flex items-center justify-self-center gap-3 2xl:gap-5">
            {visibleNav.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => `nav-pill flex items-center gap-2 ${isActive ? 'active' : ''}`}>
                <n.icon size={17} className="text-[var(--pusu-gold)]" />
                <span className="text-sm 2xl:text-base">{n.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="flex-1 xl:hidden" />

          {user ? (
            <div className="flex min-w-0 items-center gap-1.5 md:gap-3 xl:justify-self-end">
              <button onClick={() => nav('/hesap/para-yatir')} className="gold-btn px-2.5 sm:px-3 md:px-5 h-11 rounded-xl flex shrink-0 items-center gap-2 text-sm">
                <Plus size={16} /> <span className="hidden sm:inline">Para Yatır</span>
              </button>
              <div className="hidden sm:flex items-center gap-2 h-11 px-3 rounded-xl" style={{ background: 'var(--pusu-card)', border: '1px solid var(--pusu-border)' }}>
                <Wallet size={16} className="text-[var(--pusu-gold)]" />
                <div className="leading-tight">
                  <div className="text-[9px] uppercase text-gray-500">Bakiye</div>
                  <span className="font-bold gold-text text-sm">{Number(balance).toFixed(2)} ₺</span>
                </div>
              </div>
              <div className="hidden lg:flex items-center gap-2 h-11 px-3 rounded-xl" {...bonusWalletTrigger} style={{ background: 'var(--pusu-card)', border: '1px solid var(--pusu-border)', cursor: 'pointer' }}>
                <Gift size={16} className="text-yellow-400" />
                <div className="leading-tight">
                  <div className="text-[9px] uppercase text-gray-500">Bonus</div>
                  <span className="font-bold text-yellow-400 text-sm">{Number(bonusBalance).toFixed(2)} ₺</span>
                </div>
              </div>
              <UserDropdown />
            </div>
          ) : (
            <div className="site-auth-actions flex min-w-0 items-center gap-1.5 sm:gap-2 xl:justify-self-end">
              <button onClick={() => openAuth('login')} className="gold-outline-btn h-11 px-2.5 sm:px-4 md:px-5 rounded-xl text-xs sm:text-sm whitespace-nowrap">Giriş Yap</button>
              {settings.registration_open !== false && (
                <button onClick={() => openAuth('register')} className="gold-btn h-11 px-2.5 sm:px-4 md:px-5 rounded-xl text-xs sm:text-sm whitespace-nowrap">Kayıt Ol</button>
              )}
            </div>
          )}
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 xl:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <div className="mobile-nav-drawer absolute left-0 top-0 bottom-0 w-72 max-w-[88vw] pusu-panel p-5 flex flex-col gap-4 overflow-y-auto">
            <div className="flex items-center justify-between">
              <Logo size="sm" />
              <button onClick={() => setMobileOpen(false)} className="side-icon-btn inline-flex" style={{ width: 36, height: 36 }} aria-label="Menüyü kapat"><X size={16} /></button>
            </div>
            <div className="flex flex-col gap-1 mt-2">
              {visibleNav.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className={({ isActive }) => `side-link ${isActive ? 'active' : ''}`}>
                  <n.icon size={16} /> {n.label}
                </NavLink>
              ))}
              {user?.is_admin && showAdminLink && (
                <NavLink
                  to="/admin"
                  onClick={() => setMobileOpen(false)}
                  className="side-link border border-[var(--pusu-gold)]/25 bg-[var(--pusu-gold)]/5 text-[var(--pusu-accent-light)]"
                >
                  <ShieldCheck size={16} /> Yönetim Paneli
                </NavLink>
              )}
              {callbackEnabled && (
                <button onClick={() => { setMobileOpen(false); setCallbackOpen(true); }} className="side-link"><Phone size={16} /> Beni Ara</button>
              )}
            </div>
          </div>
        </div>
      )}

      <MobileBottomNav
        loggedIn={Boolean(user)}
        menuOpen={mobileOpen}
        onMenuClick={() => setMobileOpen((v) => !v)}
        onWalletClick={() => (user ? nav('/hesap/para-yatir') : openAuth('login'))}
      />

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab={authTab} registrationOpen={settings.registration_open !== false} />
      <CallbackModal open={callbackEnabled && callbackOpen} onClose={() => setCallbackOpen(false)} />
    </>
  );
};

export default Header;
